import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { EARNINGS_PAGE, sectionPath } from "./nav";
import { addOutsidePress, removeOutsidePress } from "./outsidePress";
import AffiliateEarnModal from "./AffiliateEarnModal";
import {
  loadEligibility,
  peekEligibility,
  forgetEligibility,
} from "./affiliateEligibility";

/**
 * Avatar button with a premium account dropdown: identity header + Profile,
 * Change password and Sign Out actions.
 */
function MemberMenu({ member, token, primary = "#0274F7", onProfile, onChangePassword, onAccountSettings, onMyPlans, onMobileCode, onSignOut, activePage = null }) {
  const [open, setOpen] = useState(false);
  const [hovered, setHovered] = useState(false);
  const ref = useRef(null);

  const memberToken = member?.member_token;

  /**
   * Whether to offer "Earn by sharing".
   *
   * Asked when the menu is first OPENED, never on render: this menu is on
   * every page, and most members of most memberships are not eligible, so
   * asking up front would be a request per page load to usually hear "no".
   * The answer is remembered for the tab.
   */
  const [earn, setEarn] = useState(() => peekEligibility(token, memberToken) || null);
  const [earnLoading, setEarnLoading] = useState(false);
  const [showEarn, setShowEarn] = useState(false);

  /**
   * Whether the menu is still working out what it contains.
   *
   * The affiliate entry is the only row that has to be asked for, and it used
   * to be the only row that said so: one skeleton sat among finished items and
   * then vanished again for the majority who are not eligible, so the list
   * twitched under the cursor every time it opened. The whole thing waits now
   * and settles once.
   *
   * Only while there is genuinely something to wait for. A cached answer — the
   * usual case, since the fetch starts on hover — or a member with no token to
   * ask about renders immediately: a loader for a question nobody asked is
   * slower than no loader.
   */
  const deciding = Boolean(earnLoading && !earn && token && memberToken);

  const navigate = useNavigate();

  /**
   * Open the earnings page.
   *
   * Navigated from here rather than handed down as a prop, because this menu
   * is the only place the entry exists and it already knows the membership
   * token. Threading a callback through the header's three MemberMenu call
   * sites would have been three chances to miss one.
   */
  const goToEarnings = () => {
    setOpen(false);
    setShowEarn(false);
    navigate(sectionPath(token, EARNINGS_PAGE));
  };

  // Started on HOVER as well as on open, so the row is usually already decided
  // by the time the menu appears. Without it an eligible member watches the
  // entry pop in a moment after the menu opens, shifting everything below it.
  useEffect(() => {
    if ((!open && !hovered) || earn || !token || !memberToken) {
      return;
    }

    let active = true;
    setEarnLoading(true);

    loadEligibility(token, memberToken).then((answer) => {
      if (!active) return;
      if (answer) setEarn(answer);
      setEarnLoading(false);
    });

    return () => {
      active = false;
    };
  }, [open, hovered, earn, token, memberToken]);

  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    addOutsidePress(handler);
    return () => removeOutsidePress(handler);
  }, []);

  const img = member?.img;
  const initial = (member?.name || "?").charAt(0).toUpperCase();

  const Avatar = ({ size }) => (
    <span
      className="flex shrink-0 items-center justify-center overflow-hidden rounded-full font-bold text-white"
      style={{ backgroundColor: primary, width: size, height: size, fontSize: size * 0.42 }}
    >
      {img ? <img src={img} alt="" className="h-full w-full object-cover" /> : initial}
    </span>
  );

  const itemCls =
    "flex w-full items-center gap-3 rounded-xl px-2.5 py-2 text-start text-sm font-semibold transition-colors";

  // The account page the member is ON (Spotlight renders Profile / Change
  // password / My Plans as pages): its row stays lit in the brand colour, the
  // way the nav marks the current section. Popup templates pass nothing.
  const activeProps = (key) =>
    activePage === key
      ? { "aria-current": "page", style: { background: `${primary}14`, color: primary } }
      : { onMouseEnter: onItemEnter, onMouseLeave: onItemLeave };
  const chipCls = (key) =>
    `flex h-8 w-8 items-center justify-center rounded-lg ${activePage === key ? "" : "bg-slate-100 text-slate-500"}`;
  const chipStyle = (key) => (activePage === key ? { background: `${primary}1f`, color: primary } : undefined);

  // Theme-tinted hover, matching the header "More" dropdown.
  function onItemEnter(e) {
    e.currentTarget.style.background = `${primary}0f`;
    e.currentTarget.style.color = primary;
  }
  function onItemLeave(e) {
    e.currentTarget.style.background = "";
    e.currentTarget.style.color = "";
  }

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen((o) => !o)}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        aria-label="Account menu"
        className="rounded-full ring-2 ring-surface shadow-sm transition-all duration-200 hover:shadow-md hover:scale-105 focus:outline-none focus-visible:outline-none"
        style={{ outline: (open || hovered) ? `2px solid ${primary}` : `2px solid transparent`, outlineOffset: 2 }}
      >
        <Avatar size={40} />
      </button>

      {/* Panel uses `end-0` (inset-inline-end), not `right-0` — the avatar sits
          at the inline end of the header, so in RTL a physical right-0 pinned
          the panel's right edge to the button and pushed its 16rem of width off
          the left of the viewport. */}
      {open && (
        <div className="absolute end-0 mt-2.5 w-64 max-w-[calc(100vw-1rem)] overflow-hidden rounded-2xl bg-surface shadow-[0_16px_48px_-12px_rgba(0,0,0,0.3)] ring-1 ring-black/5 z-50">
          {/* identity header */}
          <div className="flex items-center gap-3 border-b border-slate-100 bg-gradient-to-br from-slate-50 to-surface px-4 py-3.5">
            <Avatar size={44} />
            <div className="min-w-0">
              <p className="truncate text-sm font-bold text-slate-900">{member?.name || "Member"}</p>
              {member?.email && <p className="truncate text-xs text-slate-400">{member.email}</p>}
            </div>
          </div>

          {/* actions */}
          <div className="p-1.5">
            {deciding ? (
              <MenuSkeleton itemCls={itemCls} />
            ) : (
              <>
            <button
              onClick={() => {
                setOpen(false);
                onProfile?.();
              }}
              className={`${itemCls} text-slate-700`}
              {...activeProps("profile")}
            >
              <span className={chipCls("profile")} style={chipStyle("profile")}>
                <svg className="h-[18px] w-[18px]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
              </span>
              Profile
            </button>
            <button
              onClick={() => {
                setOpen(false);
                onChangePassword?.();
              }}
              className={`${itemCls} text-slate-700`}
              {...activeProps("password")}
            >
              <span className={chipCls("password")} style={chipStyle("password")}>
                <svg className="h-[18px] w-[18px]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
              </span>
              Change password
            </button>
            {/* Account settings — temporarily hidden per request (kept for later re-enable).
            <button
              onClick={() => {
                setOpen(false);
                onAccountSettings?.();
              }}
              className={`${itemCls} text-slate-700`}
              onMouseEnter={onItemEnter}
              onMouseLeave={onItemLeave}
            >
              <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-slate-100 text-slate-500">
                <svg className="h-[18px] w-[18px]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                  <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
              </span>
              Account settings
            </button>
            */}
            <button
              onClick={() => {
                setOpen(false);
                onMyPlans?.();
              }}
              className={`${itemCls} text-slate-700`}
              {...activeProps("plans")}
            >
              <span className={chipCls("plans")} style={chipStyle("plans")}>
                <svg className="h-[18px] w-[18px]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
                </svg>
              </span>
              My Plans
            </button>
            {/* Only for members a creator has actually opened products up to.
                Everyone else never sees it — a permanently dead entry in an
                account menu is worse than the feature being hard to find. */}
            {/* One row, never two. A member who is already sharing gets "My
                earnings" and nothing else: the links live on that page, one
                press away, and a menu that offers both sends a member who
                wants their money to the wrong one half the time. A member who
                has not started yet has no earnings to look at, so for them the
                single row is the invitation instead. */}
            {earn?.offer && earn.hasLinks && (
              <button
                onClick={goToEarnings}
                className={`${itemCls} text-slate-700`}
                {...activeProps(EARNINGS_PAGE)}
              >
                <span className={chipCls(EARNINGS_PAGE)} style={chipStyle(EARNINGS_PAGE)}>
                  <svg className="h-[18px] w-[18px]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 7.5v9m2.5-6.6c0-.9-1.1-1.6-2.5-1.6s-2.5.7-2.5 1.6c0 2.2 5 1.1 5 3.3 0 .9-1.1 1.6-2.5 1.6s-2.5-.7-2.5-1.6M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </span>
                My earnings
              </button>
            )}

            {/* Hidden once they have links — the earnings page carries its own
                "My sharing links" button, so this would be a second door to
                the same popup. Kept for the member who has NOT signed up:
                without it they have no way in at all. */}
            {earn?.offer && !earn.hasLinks && (
              <button
                onClick={() => {
                  setOpen(false);
                  setShowEarn(true);
                }}
                className={`${itemCls} text-slate-700`}
                onMouseEnter={onItemEnter}
                onMouseLeave={onItemLeave}
              >
                <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-slate-100 text-slate-500">
                  <svg className="h-[18px] w-[18px]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M13.828 10.172a4 4 0 00-5.656 0l-3 3a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l3-3a4 4 0 00-5.656-5.656l-1.1 1.1" />
                  </svg>
                </span>
                Earn by sharing
              </button>
            )}
            {/* Mobile Code opens a modal, not a page, so it never lights up the
                way the account pages above do — plain themed hover only. */}
            <button
              onClick={() => {
                setOpen(false);
                onMobileCode?.();
              }}
              className={`${itemCls} text-slate-700`}
              onMouseEnter={onItemEnter}
              onMouseLeave={onItemLeave}
            >
              <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-slate-100 text-slate-500">
                <svg className="h-[18px] w-[18px]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8">
                  <rect x="7" y="2.5" width="10" height="19" rx="2.5" />
                  <path strokeLinecap="round" d="M10.5 18.5h3" />
                </svg>
              </span>
              Mobile Code
            </button>
              </>
            )}
          </div>

          <div className="border-t border-slate-100 p-1.5">
            <button
              onClick={() => {
                setOpen(false);
                onSignOut?.();
              }}
              className={`${itemCls} text-red-600 dark:text-red-300 hover:bg-red-50 dark:hover:bg-red-500/15`}
            >
              <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-red-50 dark:bg-red-500/15 text-red-500">
                <svg className="h-[18px] w-[18px]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M15 12H3m0 0l4-4m-4 4l4 4M21 4v16a1 1 0 01-1 1h-6m7-18a1 1 0 00-1-1h-6" />
                </svg>
              </span>
              Sign Out
            </button>
          </div>
        </div>
      )}

      {showEarn && (
        <AffiliateEarnModal
          token={token}
          memberToken={memberToken}
          primary={primary}
          onClose={() => setShowEarn(false)}
          onSeeEarnings={goToEarnings}
          // Signing up changes the answer — drop the cached one so the menu
          // swaps the invitation for "My earnings" next time rather than
          // offering a signup they have already done.
          onChanged={() => {
            forgetEligibility(token, memberToken);
            setEarn(null);
          }}
        />
      )}
    </div>
  );
}

/**
 * The menu while it is still deciding what it holds.
 *
 * Shaped like the real rows — same height, same chip, same text offset — so
 * nothing resizes or jumps when the answer lands and the real list takes its
 * place. Five rows because that is what a member who is not an affiliate ends
 * up with; one more or less would move the panel's bottom edge.
 *
 * Sign Out is deliberately NOT part of this. It sits below the divider, it
 * needs no answer from anywhere, and putting a way out of the account behind a
 * network call for an optional feature is the wrong trade.
 */
function MenuSkeleton({ itemCls }) {
  const widths = [64, 104, 72, 88, 80];

  return (
    <div className="animate-pulse" aria-busy="true" aria-live="polite">
      <span className="sr-only">Loading your account menu</span>
      {widths.map((width, index) => (
        <div key={index} className={`${itemCls} pointer-events-none`}>
          <span className="h-8 w-8 shrink-0 rounded-lg bg-slate-100" />
          <span className="h-3 rounded bg-slate-100" style={{ width }} />
        </div>
      ))}
    </div>
  );
}

export default MemberMenu;
